import React, { useEffect, useState } from 'react';
import { X, Loader2, ImagePlus } from 'lucide-react';
import { metricsService } from '../services/metricsService';
import { isEmployee } from '../lib/roles';

/**
 * «Сообщить о проблеме» — приём обращений от пользователя.
 *
 * Оформление по токенам редизайна «Бумага»
 * (`docs/redesign/2026-09-04-bumaga/HANDOFF.md`): карточка 18, внутренняя плашка
 * 16, контрол и кнопка 10–12, чип 8; заливки вместо обводок — у карточек и
 * контролов рамок нет вовсе; вторичный текст #5a5a5a, третичный #8a8a8a;
 * акцент #E50071 и ровно один основной CTA на экран.
 *
 * Контекст (экран, запись) и версию сборки кладёт САМ интерфейс: спрашивать у
 * человека «какая у вас версия» некому и незачем, а поле, которое заполняет он,
 * будет заполнено мусором. Не собралось — отправляем без него, приём от этого не
 * отменяется.
 *
 * Дизайн контура: docs/superpowers/specs/2026-09-05-feedback-support-design.md
 */

const TYPES = [
  { id: 'bug', label: 'Что-то не работает' },
  { id: 'idea', label: 'Предложение' },
  { id: 'other', label: 'Другое' },
];

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
    + ', ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

// Скриншоты: до трёх картинок по 5 МБ, только PNG/JPEG/WebP — то же, что
// проверяет бэкенд по байтам; здесь отсев ради понятной ошибки до отправки.
const MAX_IMAGES = 3;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

// Предел текста — тот же, что у бэкенда (FeedbackIn.text). Браузер по maxLength
// молча отрезает вставленное сверх него: 02.10 длинное ТЗ пришло обрубленным на
// полуслове, и автор об этом не узнал. Поэтому у предела показываем счётчик.
const MAX_TEXT = 5000;
const TEXT_COUNTER_FROM = MAX_TEXT - 500;

const STATUS_LABEL = {
  new: 'Приняли',
  triaged: 'Разбираем',
  answered: 'Решение',
  closed: 'Закрыто',
};

export default function FeedbackDialog({ open, onClose, context, role, onAnswersRead }) {
  const [type, setType] = useState('bug');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [images, setImages] = useState([]);          // [{ file, url }] до отправки
  const [failedImages, setFailedImages] = useState(0); // сколько не прикрепилось
  const [mine, setMine] = useState([]);
  // Сообщения команды («Что нового»): приходят тем же ответом, что обращения.
  const [news, setNews] = useState([]);
  const [openNewsId, setOpenNewsId] = useState(null);
  // Канал поддержки продукта: там переписка и ход работ. 🔴 Ссылку показываем
  // ТОЛЬКО сотрудникам: канал внутренний, внешнего клиента в него не пускают, и
  // ссылка вела бы его в закрытую дверь. Клиент видит в продукте состояние и
  // финальное решение — этого достаточно, и это ровно то, что ему обещано.
  const [channelUrl, setChannelUrl] = useState(null);
  const channel = isEmployee(role) ? channelUrl : null;
  // Какое обращение раскрыто. Свёрнутый список — это оглавление: по обрезанной
  // фразе человек находит своё, а читает целиком уже раскрытым.
  const [openId, setOpenId] = useState(null);

  // Раскрыл обращение с решением — оно прочитано. Отметку ставим один раз и
  // локально гасим признак, чтобы точка в меню погасла сразу, а не через опрос.
  const openItem = (it) => {
    const next = openId === it.id ? null : it.id;
    setOpenId(next);
    if (next && it.answer && !it.answer_seen) {
      setMine((prev) => prev.map((x) => (x.id === it.id ? { ...x, answer_seen: true } : x)));
      // Счётчик перечитываем ПОСЛЕ отметки: запрос, ушедший раньше неё, получал
      // прежнее число, и точка в меню горела до следующего опроса через пять
      // минут — человек думал, что прочитанное не прочиталось (прогон 24.09).
      metricsService.markFeedbackSeen(it.id)
        .catch(() => {})
        .finally(() => onAnswersRead?.());
    }
  };

  // Раскрыл сообщение — оно прочитано; счётчик на кнопке перечитываем после
  // отметки, как и с решениями выше.
  const openNews = (it) => {
    const next = openNewsId === it.id ? null : it.id;
    setOpenNewsId(next);
    if (next && !it.seen) {
      setNews((prev) => prev.map((x) => (x.id === it.id ? { ...x, seen: true } : x)));
      metricsService.markAnnouncementSeen(it.id)
        .catch(() => {})
        .finally(() => onAnswersRead?.());
    }
  };

  useEffect(() => {
    if (!open) return;
    setSent(false);
    setError('');
    setOpenNewsId(null);
    metricsService.getMyFeedback()
      .then((d) => {
        setMine(d?.items || []);
        setNews(Array.isArray(d?.announcements) ? d.announcements : []);
        setChannelUrl(d?.support_channel_url || null);
      })
      .catch(() => { setMine([]); setNews([]); });   // списки — дополнение, не условие отправки
  }, [open]);

  // Картинки из буфера (Ctrl+V в поле) и из кнопки идут одним путём.
  const addImages = (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    let msg = '';
    const next = [...images];
    for (const f of list) {
      if (!IMAGE_TYPES.includes(f.type)) { msg = 'Только картинки: PNG, JPEG, WebP.'; continue; }
      if (f.size > MAX_IMAGE_BYTES) { msg = 'Картинка больше 5 МБ — уменьшите её.'; continue; }
      if (next.length >= MAX_IMAGES) { msg = `Не больше ${MAX_IMAGES} картинок к обращению.`; break; }
      next.push({ file: f, url: URL.createObjectURL(f) });
    }
    setImages(next);
    setError(msg);
  };
  const removeImage = (idx) => setImages((prev) => {
    URL.revokeObjectURL(prev[idx]?.url);
    return prev.filter((_, i) => i !== idx);
  });
  const onPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) { e.preventDefault(); addImages(files); }
  };

  if (!open) return null;

  const send = async () => {
    const body = text.trim();
    if (body.length < 5) {
      setError('Опишите хотя бы парой слов, что произошло.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const created = await metricsService.sendFeedback({
        type,
        text: body,
        context: context || {},
        clientVersion: process.env.REACT_APP_BUILD || null,
      });
      // Обращение уже принято: картинки — по одной, отказ любой не отменяет
      // приёма, но и не молчит — человек видит, сколько не дошло.
      let failed = 0;
      for (const img of images) {
        try {
          await metricsService.sendFeedbackAttachment(created.id, img.file);
        } catch { failed += 1; }
        URL.revokeObjectURL(img.url);
      }
      setFailedImages(failed);
      setImages([]);
      setSent(true);
      setText('');
    } catch (e) {
      // 429 — единственный отказ, который человек может исправить сам.
      setError(e?.response?.status === 429
        ? 'Слишком часто. Попробуйте через минуту.'
        : 'Не смогли принять обращение. Попробуйте ещё раз.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: 'rgba(0,0,0,.4)' }}
         role="dialog" aria-modal="true" aria-label="Сообщить о проблеме">
      {/* 🔴 На телефоне колонки встают друг под друга, а прокрутка у каждой — только
          с md: середина окна была обрезана без прокрутки, и низ формы недостижим
          (тест владельца на iPhone, 27.09). Там листается вся середина. */}
      <div className="w-full max-w-[980px] max-h-[calc(100dvh-2rem)] md:max-h-[88vh] overflow-hidden flex flex-col"
           style={{ background: '#fff', borderRadius: 18, boxShadow: '0 24px 64px rgba(0,0,0,.18)' }}>

        <div className="flex items-start justify-between px-5 pt-5 md:px-7 md:pt-7 pb-1">
          <div>
            <h2 className="text-[22px] leading-tight" style={{ fontFamily: 'Manrope, Inter, sans-serif', fontWeight: 800, letterSpacing: '-0.03em' }}>
              Сообщить о проблеме
            </h2>
            <p className="mt-2 text-[13px] leading-relaxed" style={{ color: '#5a5a5a' }}>
              Что-то сломалось или есть идея — напишите, разберём.
            </p>
          </div>
          <button onClick={onClose} aria-label="Закрыть"
                  className="shrink-0 -mr-1 -mt-1 p-2 transition-colors"
                  style={{ borderRadius: 10, color: '#8a8a8a' }}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col md:flex-row md:gap-8 px-5 pb-5 md:px-7 md:pb-7 overflow-y-auto md:overflow-hidden">
        <div className="md:w-1/2 md:overflow-y-auto">
        {sent ? (
          <div className="pt-5">
            <div className="p-5" style={{ background: '#F5F4F1', borderRadius: 16 }}>
              <p className="text-[15px] font-semibold text-black">Спасибо, получили.</p>
              <p className="mt-2 text-[13px] leading-relaxed" style={{ color: '#5a5a5a' }}>
                Решение появится здесь же, в этом окне.
                {channel && <> Ход работы и обсуждение — в канале поддержки.</>}
              </p>
              {failedImages > 0 && (
                <p className="mt-2 text-[13px] leading-relaxed" style={{ color: '#E50071' }}>
                  {failedImages} из {failedImages + (images.length)} картинок не прикрепилась —
                  текст обращения принят{channel ? ', картинку можно прислать в канал' : ''}.
                </p>
              )}
              {channel && (
                <a href={channel} target="_blank" rel="noopener noreferrer"
                   className="inline-block mt-4 text-[13px] font-semibold"
                   style={{ color: '#E50071' }}>
                  Открыть канал поддержки
                </a>
              )}
            </div>
            <button onClick={onClose}
                    className="mt-5 px-5 py-2.5 text-[13px] font-semibold"
                    style={{ background: '#EDECE8', color: '#000', borderRadius: 12 }}>
              Закрыть
            </button>
          </div>
        ) : (
          <div className="pt-5">
            <div className="flex flex-wrap gap-2.5">
              {TYPES.map((t) => (
                <button key={t.id} onClick={() => setType(t.id)}
                        className="px-4 py-2 text-[13px] font-medium transition-colors"
                        style={{
                          borderRadius: 10,
                          background: type === t.id ? '#000' : '#EDECE8',
                          color: type === t.id ? '#fff' : '#5a5a5a',
                        }}>
                  {t.label}
                </button>
              ))}
            </div>

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={onPaste}
              rows={5}
              maxLength={MAX_TEXT}
              placeholder="Что произошло? Если это ошибка — что вы делали перед ней."
              className="mt-4 w-full p-4 text-[14px] outline-none resize-none"
              style={{ background: '#F5F4F1', borderRadius: 16, lineHeight: 1.6 }}
            />
            {text.length >= TEXT_COUNTER_FROM && (
              <p className="mt-1 text-[12px]"
                 style={{ color: text.length >= MAX_TEXT ? '#E50071' : '#8a8a8a' }}>
                {text.length >= MAX_TEXT
                  ? `Поместилось ${MAX_TEXT} знаков, остальное отрезано. Длинный текст разбейте на несколько обращений.`
                  : `${text.length} из ${MAX_TEXT} знаков`}
              </p>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2.5">
              {images.map((img, i) => (
                <div key={img.url} className="relative" style={{ width: 72, height: 72 }}>
                  <img src={img.url} alt={img.file.name}
                       className="w-full h-full object-cover"
                       style={{ borderRadius: 10, background: '#F5F4F1' }} />
                  <button type="button" onClick={() => removeImage(i)}
                          aria-label={`Убрать ${img.file.name}`}
                          className="absolute -top-1.5 -right-1.5 p-1"
                          style={{ background: '#000', color: '#fff', borderRadius: 8 }}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {images.length < MAX_IMAGES && (
                <label className="inline-flex items-center gap-2 px-3 py-2 text-[12px] font-medium cursor-pointer"
                       style={{ background: '#EDECE8', color: '#5a5a5a', borderRadius: 10 }}>
                  <ImagePlus className="h-4 w-4" />
                  Скриншот
                  <input type="file" accept={IMAGE_TYPES.join(',')} multiple className="sr-only"
                         onChange={(e) => { addImages(e.target.files); e.target.value = ''; }} />
                </label>
              )}
            </div>

            {error && (
              <p className="mt-3 text-[13px]" style={{ color: '#E50071' }}>{error}</p>
            )}

            <p className="mt-4 text-[12px] leading-relaxed" style={{ color: '#8a8a8a' }}>
              К обращению приложим экран, на котором вы находитесь, и версию сборки — чтобы не
              переспрашивать. Содержимое ваших записей не передаётся. Скриншот можно вставить
              в поле из буфера (Ctrl+V) — до {MAX_IMAGES} картинок по 5 МБ.
            </p>

            <button onClick={send} disabled={sending}
                    className="mt-5 inline-flex items-center gap-2 px-6 py-3 text-[13px] font-semibold disabled:opacity-60"
                    style={{ background: '#E50071', color: '#fff', borderRadius: 12 }}>
              {sending && <Loader2 className="h-4 w-4 animate-spin" />}
              Отправить
            </button>
          </div>
        )}
        </div>

        {(mine.length > 0 || news.length > 0) && (
          <div className="md:w-1/2 md:overflow-y-auto md:pl-8 mt-6 md:mt-0"
               style={{ borderTop: 'none' }}>
            <div className="md:hidden" style={{ height: 1, background: '#F1F0EC' }} />
            {news.length > 0 && (
              <>
                <div className="pt-5 pb-3 text-[11px] uppercase" style={{ letterSpacing: '.1em', color: '#8a8a8a' }}>
                  Что нового
                </div>
                <ul className="flex flex-col gap-2.5">
                  {news.map((it) => {
                    const isOpen = openNewsId === it.id;
                    const unread = !it.seen;
                    return (
                      <li key={it.id}
                          style={unread
                            ? { background: 'rgba(229,0,113,.05)', borderRadius: 16 }
                            : { background: '#F5F4F1', borderRadius: 16 }}>
                        <button onClick={() => openNews(it)} className="w-full text-left p-4" aria-expanded={isOpen}>
                          <div className="flex items-baseline gap-3">
                            <span className="shrink-0 px-2 py-0.5 text-[11px] font-medium"
                                  style={unread
                                    ? { background: '#E50071', color: '#fff', borderRadius: 6 }
                                    : { background: '#fff', color: '#5a5a5a', borderRadius: 6 }}>
                              {unread ? 'Новое' : 'Новость'}
                            </span>
                            <span className="flex-1 min-w-0 text-[13px] font-medium text-black">{it.title}</span>
                            <span className="shrink-0 text-[11px]"
                                  style={{ color: unread ? '#E50071' : '#a0a0a0', fontWeight: unread ? 600 : 400 }}>
                              {isOpen ? 'Свернуть' : 'Читать'}
                            </span>
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4 text-[13px] leading-relaxed" style={{ color: '#5a5a5a' }}>
                            <div style={{ whiteSpace: 'pre-wrap' }}>{it.text}</div>
                            <div className="mt-2 text-[11px]" style={{ color: '#8a8a8a' }}>{formatDate(it.created_at)}</div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
            {mine.length > 0 && (
            <>
            <div className="flex items-baseline justify-between pt-5 md:pt-5 pb-3">
              <span className="text-[11px] uppercase" style={{ letterSpacing: '.1em', color: '#8a8a8a' }}>
                Ваши обращения
              </span>
              {channel && (
                <a href={channel} target="_blank" rel="noopener noreferrer"
                   className="text-[12px]" style={{ color: '#5a5a5a' }}>
                  Ход работы — в канале
                </a>
              )}
            </div>
            <ul className="flex flex-col gap-2.5">
              {mine.map((it) => {
                const isOpen = openId === it.id;
                // Непрочитанное решение видно прямо в списке: без этого счётчик в
                // меню говорит «одно», а какое именно — человек ищет вручную.
                const unread = Boolean(it.answer) && !it.answer_seen;
                return (
                  <li key={it.id}
                      style={unread
                        ? { background: 'rgba(229,0,113,.05)', borderRadius: 16 }
                        : { background: '#F5F4F1', borderRadius: 16 }}>
                    <button onClick={() => openItem(it)}
                            className="w-full text-left p-4"
                            aria-expanded={isOpen}>
                      <div className="flex items-baseline gap-3">
                        <span className="shrink-0 px-2 py-0.5 text-[11px] font-medium"
                              style={unread
                                ? { background: '#E50071', color: '#fff', borderRadius: 6 }
                                : { background: '#fff', color: '#5a5a5a', borderRadius: 6 }}>
                          {unread ? 'Новое решение' : (STATUS_LABEL[it.status] || it.status)}
                        </span>
                        <span className={`flex-1 min-w-0 text-[13px] text-black ${isOpen ? '' : 'truncate'}`}
                              style={isOpen ? { lineHeight: 1.6, whiteSpace: 'pre-wrap' } : undefined}>
                          {it.text}
                        </span>
                        <span className="shrink-0 text-[11px]"
                              style={{ color: unread ? '#E50071' : '#a0a0a0',
                                       fontWeight: unread ? 600 : 400 }}>
                          {isOpen ? 'Свернуть' : 'Читать'}
                        </span>
                      </div>
                      {isOpen && (
                        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px]"
                             style={{ color: '#8a8a8a' }}>
                          <span>{formatDate(it.created_at)}</span>
                          {/* Короткий номер — тот же, что ушёл в канал: по нему человек
                              и ссылается на своё обращение в разговоре. */}
                          <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                            обращение {String(it.id || '').slice(0, 8)}
                          </span>
                        </div>
                      )}
                    </button>

                    {isOpen && (
                      <div className="px-4 pb-4">
                        {it.answer ? (
                          <div className="p-4 text-[13px] leading-relaxed"
                               style={{ background: '#fff', color: '#000', borderRadius: 12 }}>
                            <div className="text-[11px] uppercase mb-2"
                                 style={{ letterSpacing: '.1em', color: '#8a8a8a' }}>Что решили</div>
                            <div style={{ whiteSpace: 'pre-wrap' }}>{it.answer}</div>
                          </div>
                        ) : (
                          <div className="text-[12px] leading-relaxed" style={{ color: '#8a8a8a' }}>
                            Решения пока нет. Когда появится — покажем здесь.
                            {channel && <> Ход работы идёт в канале поддержки.</>}
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            </>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
